// SP2 (Mail-Filterleiste): pure Filterlogik der Liste bzw. der Suchtreffer —
// bewusst aus MailView.tsx herausgelöst, damit sie in der Vitest-Suite testbar
// ist (analog zu mailSearch.ts aus SP1).
//
// Text- und Zeitraum-Filter wirken clientseitig auf die geladene Liste und sind
// mit der globalen Suche kombinierbar. Der Filter „Vorgänge" (L1) ist seit
// 23.08.2026 anders gebaut: Er SIEBT nicht mehr, sondern MailView tauscht die
// Listenquelle gegen die serverseitig verknüpften Mails des Postfachs aus
// (`GET /mail/links/verknuepft`, siehe services/vorgangsMails.ts). `verknuepft`
// bleibt hier trotzdem der maßgebliche Schlüsselsatz — er stammt dann aus
// genau dieser Quelle, ist also per Konstruktion deckungsgleich mit ihr, und
// hält Text-/Zeitraum-Filter und Vorgangsbezug in EINER Auswertung zusammen.
import type { Email } from "../../services/graphMailService";

/**
 * Zeitraum-Chip der Filterleiste. User-Entscheidung (Review 2026-07-05, §20):
 * „Heute" / „Gestern" / „Letzte 7 Tage". `null` = kein Zeitraum-Filter.
 */
export type MailDateRange = "today" | "yesterday" | "last7" | null;

/** Zustand der Filterleiste: freier Absender-/Empfänger-Text + optionaler Zeitraum. */
export type MailFilterState = {
    /** Absender/Empfänger — matcht senderName, senderEmail sowie to[] (Name + Adresse). */
    text: string;
    range: MailDateRange;
    /**
     * L1 — „Vorgänge": nur Mails zeigen, die zu einem Vorgang gehören. Optional,
     * damit bestehende Filter-Objekte (und ihre Tests) unverändert gültig
     * bleiben; fehlt das Feld, ist der Filter aus.
     */
    nurVorgaenge?: boolean;
};

/**
 * L1 — die verknüpften Nachrichten des Postfachs, abgeleitet aus dem, was
 * `GET /mail/links/verknuepft` geliefert hat. Zwei Schlüssel, weil die Liste
 * Konversationen zusammenfasst: der angezeigte Repräsentant eines Threads ist
 * oft eine ANDERE Nachricht als die, die seinerzeit verknüpft wurde — über die
 * `conversationId` wird der Thread trotzdem als „gehört zu einem Vorgang" erkannt.
 */
export type VerknuepfteMails = {
    messageIds: ReadonlySet<string>;
    conversationIds: ReadonlySet<string>;
};

/** Neutralzustand (kein Filter aktiv). */
export const EMPTY_MAIL_FILTER: MailFilterState = { text: "", range: null, nurVorgaenge: false };

/** true, sobald mindestens ein Filter greift — steuert Badge „Filter aktiv" + Zurücksetzen. */
export function isMailFilterActive(f: MailFilterState): boolean {
    return f.text.trim().length > 0 || f.range !== null || !!f.nurVorgaenge;
}

/**
 * Kalender-Tagesabstand (0 = heute, 1 = gestern, …) — deckungsgleich mit
 * `getDateGroup` in MailView, damit Filter und Datumsgruppen konsistent bleiben.
 */
function dayDiff(emailDate: Date, ref: Date): number {
    const refDay = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate());
    const emlDay = new Date(emailDate.getFullYear(), emailDate.getMonth(), emailDate.getDate());
    return Math.floor((refDay.getTime() - emlDay.getTime()) / 86400000);
}

function matchesRange(emailDate: Date, range: MailDateRange, ref: Date): boolean {
    if (!range) return true;
    if (Number.isNaN(emailDate.getTime())) return false;
    const d = dayDiff(emailDate, ref);
    if (range === "today") return d === 0;
    if (range === "yesterday") return d === 1;
    // „Letzte 7 Tage" = alle jüngeren Mails (heute … 7 Tage zurück) — schließt
    // die Gruppen „Heute"/„Gestern"/„Letzte 7 Tage" ein.
    return d >= 0 && d <= 7;
}

function matchesText(email: Email, needle: string): boolean {
    if (!needle) return true;
    const parts: string[] = [email.senderName, email.senderEmail];
    for (const a of email.to ?? []) {
        if (a?.name) parts.push(a.name);
        if (a?.address) parts.push(a.address);
    }
    return parts.filter(Boolean).join(" ").toLowerCase().includes(needle);
}

/**
 * L1 — gehört diese Mail zu einem Vorgang? Das wird NICHT geraten, sondern
 * anhand der Liste beantwortet, die das Backend liefert (L2). Solange sie noch
 * nicht da ist (`undefined`), gilt nichts als verknüpft: lieber eine noch leere
 * als eine falsche Trefferliste.
 */
function matchesVorgang(email: Email, verknuepft: VerknuepfteMails | undefined): boolean {
    if (!verknuepft) return false;
    if (verknuepft.messageIds.has(email.id)) return true;
    return !!email.conversationId && verknuepft.conversationIds.has(email.conversationId);
}

/**
 * Wendet die Filterleiste clientseitig auf die geladene Liste bzw. auf die
 * Suchtreffer an. Reihenfolge/Objekt-Identität bleiben erhalten (kein Re-Sort).
 *
 * `verknuepft` speist den Filter „Vorgänge" (L1) — ohne den Parameter bleibt er
 * wirkungslos bzw. leer, siehe `matchesVorgang`.
 */
export function applyMailFilter<T extends Email>(
    emails: T[],
    filter: MailFilterState,
    ref: Date = new Date(),
    verknuepft?: VerknuepfteMails,
): T[] {
    const needle = filter.text.trim().toLowerCase();
    if (!needle && !filter.range && !filter.nurVorgaenge) return emails;
    return emails.filter(
        (e) =>
            matchesRange(e.date, filter.range, ref) &&
            matchesText(e, needle) &&
            (!filter.nurVorgaenge || matchesVorgang(e, verknuepft)),
    );
}
